package com.exura.pages.publicplans.orders

import androidx.compose.runtime.Composable
import com.exura.ui.Assets
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.ChartsRawPastelPalette
import com.exura.ChartsRawPastelPalette.toCssRgba
import com.exura.chartsJS.ComposeChart
import com.exura.components.buttons.AppButton
import com.exura.components.buttons.ButtonGhostVariant
import com.exura.components.icons.UntiledIcon
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraBodySizeTextVariant
import com.exura.components.pageTypography.ExuraEmphasisedTextVariant
import com.exura.components.pageTypography.ExuraPrimaryControlTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.ExuraUnderwriteTextVariant
import com.exura.features.publicplans.domain.model.AnonymousPublicPlanOrder
import com.exura.features.publicplans.publicPlansLocalization
import com.exura.util.jso
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.datetime.LocalDate

enum class OrdersPreviewRangeMode {
    DAY,
    WEEK,
    MONTH
}

data class OrderStats(
    val date: LocalDate,
    var accessedCount: Int = 0,
    var startedCount: Int = 0,
    var endedCount: Int = 0,
    var inProgressCount: Int = 0
)


@Composable
internal fun PublicPlanOrdersPreview(
    orders: List<AnonymousPublicPlanOrder>,
    modifier: Modifier = Modifier,
) {

    var selectedRangeMode by remember { mutableStateOf(OrdersPreviewRangeMode.DAY) }

    Column(
        modifier = Modifier.fillMaxWidth().then(modifier),
    ) {
        OrdersPreviewControls(
            selectedRangeMode = selectedRangeMode,
            onRangeModeChange = { selectedRangeMode = it },
            modifier = Modifier
        )
        OrdersPreviewGraphArea(
            orders = orders,
            selectedRangeMode = selectedRangeMode,
            modifier = Modifier
        )
    }
}

@Composable
private fun OrdersPreviewControls(
    modifier: Modifier = Modifier,
    selectedRangeMode: OrdersPreviewRangeMode,
    onRangeModeChange: (OrdersPreviewRangeMode) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().then(modifier),
        horizontalArrangement = Arrangement.spacedBy(25.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = publicPlansLocalization.current.usage.range,
            modifier = ExuraTextStyle.toModifier(ExuraBodyHeaderTextVariant),
        )
        OrdersPreviewRangeOption(
            iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ORDER_RANGE_OPTION_DAY,
            text = publicPlansLocalization.current.usage.rangeDay,
            selectedRangeMode = selectedRangeMode,
            optionMode = OrdersPreviewRangeMode.DAY,
            onRangeModeChange = onRangeModeChange,
        )
        OrdersPreviewRangeOption(
            iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ORDER_RANGE_OPTION_WEEK,
            text = publicPlansLocalization.current.usage.rangeWeek,
            selectedRangeMode = selectedRangeMode,
            optionMode = OrdersPreviewRangeMode.WEEK,
            onRangeModeChange = onRangeModeChange,
        )
        OrdersPreviewRangeOption(
            iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ORDER_RANGE_OPTION_MONTH,
            text = publicPlansLocalization.current.usage.rangeMonth,
            selectedRangeMode = selectedRangeMode,
            optionMode = OrdersPreviewRangeMode.MONTH,
            onRangeModeChange = onRangeModeChange,
        )
    }
}

@Composable
private fun OrdersPreviewRangeOption(
    modifier: Modifier = Modifier,
    iconSrc: String,
    text: String,
    selectedRangeMode: OrdersPreviewRangeMode,
    optionMode: OrdersPreviewRangeMode,
    onRangeModeChange: (OrdersPreviewRangeMode) -> Unit,
) = AppButton(
    text = text,
    onClick = { onRangeModeChange(optionMode) },
    variant = ButtonGhostVariant,
    modifier = ExuraTextStyle
        .toModifier(
            if (selectedRangeMode == optionMode) ExuraEmphasisedTextVariant
                .then(ExuraPrimaryControlTextVariant)
            else ExuraBodySizeTextVariant
        )
        .then(modifier),
    icon = { UntiledIcon(iconSrc) }
)


@Composable
private fun OrdersPreviewGraphArea(
    orders: List<AnonymousPublicPlanOrder>,
    selectedRangeMode: OrdersPreviewRangeMode,
    modifier: Modifier = Modifier,
) {
    val chartData = remember(orders, selectedRangeMode) { calculateOrderStats(orders, selectedRangeMode) }
    Box(
        modifier = ExuraTextStyle.toModifier(ExuraUnderwriteTextVariant)
            .fillMaxWidth()
            .then(modifier),
        contentAlignment = Alignment.Center,
    ) {
        if (chartData.isNotEmpty()) {
            ComposeChart {
                // Import the palette for easier access
                val palette = ChartsRawPastelPalette

                configLegend(position = "top", padding = 50)
                configScales {
                    x { grid(false) }
                    y { ticks { precision = 0 } }
                }

                labels(*chartData.map { it.date.toString() }.toTypedArray())

                // Data mapping
                val started = chartData.map { it.startedCount }
                val ended = chartData.map { it.endedCount }
                val accessed = chartData.map { it.accessedCount }
                val inProgress = chartData.map { it.inProgressCount }

                val barWidth = 1.0
                val barRound = 10
                val barOrder = 1

                bar(started) {
                    label = publicPlansLocalization.current.usage.started
                    backgroundColor = palette.Teal.toCssRgba() // #4DB6AC
                    barPercentage = barWidth
                    borderRadius = barRound
                    order = barOrder
                }
                bar(ended) {
                    label = publicPlansLocalization.current.usage.finished
                    backgroundColor = palette.Red.toCssRgba() // #E57373
                    barPercentage = barWidth
                    borderRadius = barRound
                    order = barOrder
                }
                bar(accessed) {
                    label = publicPlansLocalization.current.usage.downloaded
                    backgroundColor = palette.Blue.toCssRgba() // #64B5F6
                    barPercentage = barWidth
                    borderRadius = barRound
                    order = barOrder
                }

                line(inProgress) {
                    label = publicPlansLocalization.current.usage.inProgress

                    // Solid orange for border
                    borderColor = palette.Orange.toCssRgba()
                    borderWidth = 3

                    // 20% opacity orange for fill
                    backgroundColor = palette.Orange.copyf(alpha = 0.2f).toCssRgba()
                    fill = true

                    pointRadius = 0
                    pointHoverRadius = 6
                    tension = 0.2
                    order = 2
                }

                configPlugin("annotation") { pluginCtx ->
                    pluginCtx.annotations = jso {
                        this["todayLine"] = jso {
                            this.type = "line"
                            this.scaleID = "x"
                            this.value = todayAnnotationDate(selectedRangeMode).toString()

                            this.borderColor = palette.Grey.toCssRgba() // #757575
                            this.borderWidth = 2
                            this.borderDash = arrayOf(6, 6)

                            this.label = jso {
                                this.display = true
                                this.content = publicPlansLocalization.current.usage.timeMarkToday
                                this.position = "start"
                                // 70% opacity Grey for background
                                this.backgroundColor = palette.Grey.copyf(alpha = 0.7f).toCssRgba()
                                this.color = palette.BaseLight.toCssRgba()
                                this.font = jso {
                                    this.size = 10
                                    this.weight = "bold"
                                }
                            }
                        }
                    }
                }
            }
        } else {
            SpanText(
                text = publicPlansLocalization.current.usage.noDataToDisplay,
                modifier = ExuraTextStyle.toModifier(ExuraBodySizeTextVariant),
            )
        }
    }
}
